import React from "react";
import ReactCountryFlag from "react-country-flag";
import { useUser } from "../../../../contexts/UserContext";
import { navigate } from "gatsby";
import { useSelectedPlanContext } from "../../../../contexts/SelectedPlanContext";
import { usePageSettings } from "../../../../contexts/PageSettingsContext";
import { useFormikContext } from "formik";
import { TertiaryOutlineButton } from "../../inputs/buttons/Button";
import { useFetchTotalPrice } from "../../../../hooks/useFetchTotalPrice";
import { useOrderDiscount } from "../../../../hooks/useOrderDiscount";
import QuickbuyLoading from "../../Quickbuy/QuickbuyLoading";

const OrderConfirmationSummary = ({ orderNumber, pageContext }) => {
	const { activeProfile, logout } = useUser(); // Updated context
	const { selectedPlan } = useSelectedPlanContext();
	const { selectedLocation, translations } = usePageSettings();
	const { values } = useFormikContext();

	const addonIds = [];
	if (values?.configuration?.os?.id) addonIds.push(values.configuration.os.id);
	if (values?.configuration?.additionalIp?.id) addonIds.push(values.configuration.additionalIp.id);
	if (values?.configuration?.bgp?.enabled && values.configuration.bgp.id) addonIds.push(values.configuration.bgp.id);

	const rawPaymentCycle = values?.configuration?.paymentCycle;
	const translatedPaymentCycle = translations[`paymentCycle.${rawPaymentCycle}`] || rawPaymentCycle;

	const { data: totalPrice, isLoading: totalPriceLoading } = useFetchTotalPrice({
		addonIds,
		paymentCycle: rawPaymentCycle,
	});
	const orderId = orderNumber;

	const discountPricing = useOrderDiscount({
		profileId: activeProfile?.id,
		orderId,
	});

	const handleOnLogout = (event) => {
		event.preventDefault();
		logout();
		navigate("/");
	};

	if (totalPriceLoading || discountPricing.isLoading) {
		return (
			<div className="min-h-[420px] flex items-center justify-center">
				<QuickbuyLoading loadingType="discount" message="Processing order…" service={selectedPlan?.service} />
			</div>
		);
	}

	function formatCustomDate(date = new Date()) {
		const month = date.getMonth() + 1;
		const day = date.getDate();
		const year = date.getFullYear();
		const hours = date.getHours();
		const minutes = date.getMinutes();
		const seconds = date.getSeconds();

		const pad = (num) => String(num).padStart(2, "0");

		return `${month}/${day}/${year} - ${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
	}
	const userName = activeProfile ? activeProfile.name + " " + activeProfile.lastname : "Welcome";

	const displayServiceTitle = translations[`serviceLabel.${selectedPlan.service}`] || selectedPlan.service;
	const displayWebHostingPlane = translations[`webHostingPlan.${selectedPlan.name}`] || "N/A";

	// 1. Render summary information depending on the service.
	const renderOrderInformation = () => {
		if (!selectedPlan) return null; // If no plan is selected, return nothing
		switch (selectedPlan.service) {
			case "vps":
				return (
					<>
						<div className="flex flex-col gap-4 w-full py-4 px-7">
							<div className="flex justify-between">
								<span className="text-neutral-50 text-sm font-bold leading-none">
									{translations["CheckoutSuccessModalOrderSummary"]}
								</span>
								<span className="text-xs font-normal"> {formatCustomDate()}</span>
							</div>

							<div className="flex flex-col gap-1 pb-2 border-b border-dashed border-ipxon-light-blue-gray">
								<div className="flex justify-between">
									<span className="order-summary-information">
										{translations["CheckoutSuccessModalOrderSummaryProduct"]}
									</span>
									<span className="order-summary-information capitalize">
										{displayServiceTitle || "No plan Selected"}
									</span>
								</div>

								<div className="flex justify-between">
									<span className="order-summary-information">
										{translations["CheckoutSuccessModalOrderSummaryQuantity"]}
									</span>
									<span className="order-summary-information capitalize">
										{values.orderCount || "N/A"}
									</span>
								</div>

								<div className="flex justify-between">
									<span className="order-summary-information">
										{translations["CheckoutSuccessModalOrderSummaryLocation"]}
									</span>
									<div className="pl-1 flex gap-2 items-center rounded-lg w-fit text-zinc-200 text-sm">
										<ReactCountryFlag
											style={{
												fontSize: "1.15em",
												lineHeight: "1.15em",
												borderRadius: "80%",
												objectFit: "cover",
											}}
											countryCode={selectedLocation.iso_code}
											svg
										/>
										<span className="order-summary-information">{selectedLocation.name}</span>
									</div>
								</div>
							</div>

							<ul className="text-neutral-50 flex flex-col gap-1 text-sm pb-2 border-b border-dashed border-ipxon-light-blue-gray">
								<li>
									<h4 className="font-bold text-xs pb-1">
										{translations["CheckoutSuccessModalResources"]}
									</h4>
								</li>
								<li className="flex w-full justify-between">
									<span className="order-summary-information">
										{translations["CheckoutSuccessModalCPU"]}
									</span>
									<span className="order-summary-information">{selectedPlan.cpu?.value}</span>
								</li>
								<li className="flex w-full justify-between">
									<span className="order-summary-information">
										{translations["CheckoutSuccessModalRam"]}
									</span>
									<span className="order-summary-information">{selectedPlan.ram?.value}</span>
								</li>
								<li className="flex w-full justify-between">
									<span className="order-summary-information">
										{translations["CheckoutSuccessModalStorage"]}
									</span>
									<span className="order-summary-information">{selectedPlan.storage?.value}</span>
								</li>
								<li className="flex w-full justify-between">
									<span className="order-summary-information">
										{translations["CheckoutSuccessModalBandwidth"]}
									</span>
									<span className="order-summary-information">{selectedPlan.bandwidth?.value}</span>
								</li>
								<li className="flex w-full justify-between">
									<span className="text-zinc-400  font-normal">
										{translations["CheckoutSuccessModalTransfer"]}
									</span>
									<span className="order-summary-information">{selectedPlan.transfer?.value}</span>
								</li>
							</ul>

							<ul className="text-neutral-50 flex flex-col gap-1 text-sm">
								<li>
									<h4 className="font-bold text-xs pb-1">
										{translations["CheckoutSuccessModalSettings"]}
									</h4>
								</li>

								{values.configuration.serverName ? (
									<li className="flex w-full justify-between">
										<span className="order-summary-information">
											{translations["CheckoutSuccessModalHostname"]}
										</span>
										<span>{values.configuration.serverName}</span>
									</li>
								) : null}

								{values.configuration.bgp?.enabled ? (
									<li className="flex w-full justify-between">
										<span className="order-summary-information">
											{translations["CheckoutSuccessModalBGPSession"]}
										</span>
										<span>SI</span>
									</li>
								) : null}
								{values.configuration.additionalIp?.name ? (
									<li className="flex w-full justify-between">
										<span className="order-summary-information">
											{translations["CheckoutSuccessModalAdditionalIP"]}
										</span>
										<span>{values.configuration.additionalIp.name}</span>
									</li>
								) : null}
								{values.configuration.os?.name ? (
									<li className="flex w-full justify-between">
										<span className="order-summary-information">
											{translations["CheckoutSuccessModalSO"]}
										</span>
										<span>{values.configuration.os.name}</span>
									</li>
								) : null}
							</ul>

							<ul className="flex flex-col gap-1">
								<li className="flex justify-between w-full">
									<span className="order-summary-information">
										{translations["CheckoutSuccessModalPaymentInstall"]}
									</span>
									<span className="text-zinc-400 text-sm capitalize">
										{translations["CheckoutSuccessModalPaymentInstallIncluded"]}
									</span>
								</li>

								<li className="flex justify-between w-full">
									<span className="order-summary-information">
										{translations["CheckoutSuccessModalPaymentCycle"]}
									</span>
									<span className="text-zinc-400 text-sm capitalize">{translatedPaymentCycle}</span>
								</li>

								{/* SUBTOTAL */}
								<li className="flex justify-between w-full mt-4">
									<span className="order-summary-information">
										{translations["CheckoutSuccessModalSubtotal"] ?? "Subtotal"}
									</span>
									<span className="text-zinc-400 text-sm uppercase">{totalPrice}</span>
								</li>

								{/* DISCOUNT (IF ANY) */}
								{discountPricing.discount && (
									<li className="flex justify-between w-full">
										<span className="order-summary-information">
											{translations["CheckoutSuccessModalDiscountApplied"] ??
												"Cupón de descuento aplicado"}
										</span>
										<span className="text-zinc-400 text-sm">{discountPricing.discount}</span>
									</li>
								)}
							</ul>
						</div>

						<div className="flex justify-between w-full px-7 py-4 bg-zinc-900 rounded-bl-lg rounded-br-lg">
							<span>{translations["CheckoutSuccessModalTotal"]}</span>
							<span className="uppercase">{discountPricing.total || totalPrice}</span>
						</div>
					</>
				);

			case "baremetal":
				return (
					<>
						<div className="flex flex-col gap-4 w-full py-4 px-7">
							<div className="flex justify-between">
								<span className="text-neutral-50 text-sm font-bold leading-none">
									{translations["CheckoutSuccessModalOrderSummary"]}
								</span>
								<span className="text-xs font-normal"> {formatCustomDate()}</span>
							</div>

							<div className="flex flex-col gap-1 pb-2 border-b border-dashed border-ipxon-light-blue-gray">
								<div className="flex justify-between">
									<span className="order-summary-information">
										{translations["CheckoutSuccessModalOrderSummaryProduct"]}
									</span>
									<span className="order-summary-information capitalize">
										{displayServiceTitle || "No plan Selected"}
									</span>
								</div>

								<div className="flex justify-between">
									<span className="order-summary-information">
										{translations["CheckoutSuccessModalOrderSummaryQuantity"]}
									</span>
									<span className="order-summary-information capitalize">
										{values.orderCount || "N/A"}
									</span>
								</div>

								<div className="flex justify-between">
									<span className="order-summary-information">
										{translations["CheckoutSuccessModalOrderSummaryLocation"]}
									</span>
									<div className="pl-1 flex gap-2 items-center rounded-lg w-fit text-zinc-200 text-sm">
										<ReactCountryFlag
											style={{
												fontSize: "1.15em",
												lineHeight: "1.15em",
												borderRadius: "80%",
												objectFit: "cover",
											}}
											countryCode={selectedLocation.iso_code}
											svg
										/>
										<span className="order-summary-information">{selectedLocation.name}</span>
									</div>
								</div>
							</div>

							<ul className="text-neutral-50 flex flex-col gap-1 text-sm pb-2 border-b border-dashed border-ipxon-light-blue-gray">
								<li>
									<h4 className="font-bold text-xs pb-1">
										{translations["CheckoutSuccessModalResources"]}
									</h4>
								</li>
								<li className="flex w-full justify-between">
									<span className="order-summary-information">
										{translations["CheckoutSuccessModalCPU"]}
									</span>
									<span className="order-summary-information">{selectedPlan.cpu?.value}</span>
								</li>
								<li className="flex w-full justify-between">
									<span className="order-summary-information">
										{translations["CheckoutSuccessModalRam"]}
									</span>
									<span className="order-summary-information">{selectedPlan.ram?.value}</span>
								</li>
								<li className="flex w-full justify-between">
									<span className="order-summary-information">
										{translations["CheckoutSuccessModalStorage"]}
									</span>
									<span className="order-summary-information">{selectedPlan.storage?.value}</span>
								</li>
								<li className="flex w-full justify-between">
									<span className="order-summary-information">
										{translations["CheckoutSuccessModalBandwidth"]}
									</span>
									<span className="order-summary-information">{selectedPlan.bandwidth?.value}</span>
								</li>
								<li className="flex w-full justify-between">
									<span className="text-zinc-400  font-normal">
										{translations["CheckoutSuccessModalTransfer"]}
									</span>
									<span className="order-summary-information">{selectedPlan.transfer?.value}</span>
								</li>
							</ul>

							<ul className="text-neutral-50 flex flex-col gap-1 text-sm">
								{(values.configuration.bgp?.enabled ||
									values.configuration.additionalIp?.name ||
									values.configuration.os?.name) && (
									<li>
										<h4 className="font-bold text-xs pb-1">
											{translations["CheckoutSuccessModalSettings"]}
										</h4>
									</li>
								)}
								{values.configuration.serverName ? (
									<li className="flex w-full justify-between">
										<span className="order-summary-information">
											{translations["CheckoutSuccessModalHostname"]}
										</span>
										<span>{values.configuration.serverName}</span>
									</li>
								) : null}

								{values.configuration.bgp?.enabled ? (
									<li className="flex w-full justify-between">
										<span className="order-summary-information">
											{translations["CheckoutSuccessModalBGPSession"]}
										</span>
										<span>SI</span>
									</li>
								) : null}
								{values.configuration.additionalIp?.name ? (
									<li className="flex w-full justify-between">
										<span className="order-summary-information">
											{translations["CheckoutSuccessModalAdditionalIP"]}
										</span>
										<span>{values.configuration.additionalIp.name}</span>
									</li>
								) : null}
								{values.configuration.os?.name ? (
									<li className="flex w-full justify-between">
										<span className="order-summary-information">
											{translations["CheckoutSuccessModalSO"]}
										</span>
										<span>{values.configuration.os.name}</span>
									</li>
								) : null}
							</ul>

							<ul className="flex flex-col gap-1">
								<li className="flex justify-between w-full">
									<span className="order-summary-information">
										{translations["CheckoutSuccessModalPaymentInstall"]}
									</span>
									<span className="text-zinc-400 text-sm capitalize">
										{translations["CheckoutSuccessModalPaymentInstallIncluded"]}
									</span>
								</li>

								<li className="flex justify-between w-full">
									<span className="order-summary-information">
										{translations["CheckoutSuccessModalPaymentCycle"]}
									</span>
									<span className="text-zinc-400 text-sm capitalize">{translatedPaymentCycle}</span>
								</li>
								{/* SUBTOTAL */}
								<li className="flex justify-between w-full mt-4">
									<span className="order-summary-information">
										{translations["CheckoutSuccessModalSubtotal"] ?? "Subtotal"}
									</span>
									<span className="text-zinc-400 text-sm uppercase">{totalPrice}</span>
								</li>

								{/* DISCOUNT (IF ANY) */}
								{/* {discountPricing.discount && (
									<li className="flex justify-between w-full">
										<span className="order-summary-information">
											{translations["CheckoutSuccessModalDiscountApplied"] ??
												"Cupón de descuento aplicado"}
										</span>
										<span className="text-zinc-400 text-sm">{discountPricing.discount}</span>
									</li>
								)} */}
								{discountPricing.hasDiscount && (
									<li className="flex justify-between w-full">
										<span className="order-summary-information">Cupón de descuento aplicado</span>
										<span className="text-zinc-400 text-sm">{discountPricing.discount}</span>
									</li>
								)}
							</ul>
						</div>

						<div className="flex justify-between w-full px-7 py-4 bg-zinc-900 rounded-bl-lg rounded-br-lg">
							<span>{translations["CheckoutSuccessModalTotal"]}</span>
							<span className="uppercase">{discountPricing.total || totalPrice}</span>
						</div>
					</>
				);

			case "colocation":
				return (
					<>
						<div className="flex flex-col gap-4 w-full py-4 px-7">
							<div className="flex justify-between">
								<span className="text-neutral-50 text-sm font-bold leading-none">
									{translations["CheckoutSuccessModalOrderSummary"]}
								</span>
								<span className="text-xs font-normal"> {formatCustomDate()}</span>
							</div>

							<div className="flex flex-col gap-1 pb-2 border-b border-dashed border-ipxon-light-blue-gray">
								<div className="flex justify-between">
									<span className="order-summary-information">
										{translations["CheckoutSuccessModalOrderSummaryProduct"]}
									</span>
									<span className="order-summary-information capitalize">
										{displayServiceTitle || "No plan Selected"}
									</span>
								</div>

								<div className="flex justify-between">
									<span className="order-summary-information">
										{translations["CheckoutSuccessModalOrderSummaryQuantity"]}
									</span>
									<span className="order-summary-information capitalize">
										{values.orderCount || "N/A"}
									</span>
								</div>

								<div className="flex justify-between">
									<span className="order-summary-information">
										{translations["CheckoutSuccessModalOrderSummaryLocation"]}
									</span>
									<div className="pl-1 flex gap-2 items-center rounded-lg w-fit text-zinc-200 text-sm">
										<ReactCountryFlag
											style={{
												fontSize: "1.15em",
												lineHeight: "1.15em",
												borderRadius: "80%",
												objectFit: "cover",
											}}
											countryCode={selectedLocation.iso_code}
											svg
										/>
										<span className="order-summary-information">{selectedLocation.name}</span>
									</div>
								</div>
							</div>

							<ul className="text-neutral-50 flex flex-col gap-1 text-sm pb-2 border-b border-dashed border-ipxon-light-blue-gray">
								<li>
									<h4 className="font-bold text-xs pb-1">
										{translations["CheckoutSuccessModalResources"]}
									</h4>
								</li>
								<li className="flex w-full justify-between">
									<span className="order-summary-information">Rack:</span>
									<span className="order-summary-information">{selectedPlan.rack?.value}</span>
								</li>
								<li className="flex w-full justify-between">
									<span className="order-summary-information">Energy:</span>
									<span className="order-summary-information">{selectedPlan.energy?.value}</span>
								</li>
								<li className="flex w-full justify-between">
									<span className="order-summary-information">Bandwidth:</span>
									<span className="order-summary-information">{selectedPlan.bandwidth?.value}</span>
								</li>
								<li className="flex w-full justify-between">
									<span className="order-summary-information">Prefix:</span>
									<span className="order-summary-information">{selectedPlan.prefix?.value}</span>
								</li>
							</ul>

							<ul className="text-neutral-50 flex flex-col gap-1 text-sm">
								{values.configuration.bgp?.enabled ? (
									<li>
										<h4 className="font-bold text-xs pb-1">
											{translations["CheckoutSuccessModalSettings"]}
										</h4>
									</li>
								) : null}

								{values.configuration.bgp?.enabled ? (
									<li className="flex w-full justify-between">
										<span className="order-summary-information">
											{translations["CheckoutSuccessModalBGPSession"]}
										</span>
										<span>SI</span>
									</li>
								) : null}
								{values.configuration.additionalIp?.name ? (
									<li className="flex w-full justify-between">
										<span className="order-summary-information">
											{translations["CheckoutSuccessModalAdditionalIP"]}
										</span>
										<span>{values.configuration.additionalIp.name}</span>
									</li>
								) : null}
								{values.configuration.os?.name ? (
									<li className="flex w-full justify-between">
										<span className="order-summary-information">
											{translations["CheckoutSuccessModalSO"]}
										</span>
										<span>{values.configuration.os.name}</span>
									</li>
								) : null}
							</ul>

							<ul className="flex flex-col gap-1">
								<li className="flex justify-between w-full">
									<span className="order-summary-information">
										{translations["CheckoutSuccessModalPaymentInstall"]}
									</span>
									<span className="text-zinc-400 text-sm capitalize">
										{translations["CheckoutSuccessModalPaymentInstallIncluded"]}
									</span>
								</li>

								<li className="flex justify-between w-full">
									<span className="order-summary-information">
										{translations["CheckoutSuccessModalPaymentCycle"]}
									</span>
									<span className="text-zinc-400 text-sm capitalize">{translatedPaymentCycle}</span>
								</li>

								{/* SUBTOTAL */}
								<li className="flex justify-between w-full mt-4">
									<span className="order-summary-information">
										{translations["CheckoutSuccessModalSubtotal"] ?? "Subtotal"}
									</span>
									<span className="text-zinc-400 text-sm uppercase">{totalPrice}</span>
								</li>

								{/* DISCOUNT (IF ANY) */}
								{discountPricing.discount && (
									<li className="flex justify-between w-full">
										<span className="order-summary-information">
											{translations["CheckoutSuccessModalDiscountApplied"] ??
												"Cupón de descuento aplicado"}
										</span>
										<span className="text-zinc-400 text-sm">{discountPricing.discount}</span>
									</li>
								)}
							</ul>
						</div>

						<div className="flex justify-between w-full px-7 py-4 bg-zinc-900 rounded-bl-lg rounded-br-lg">
							<span>{translations["CheckoutSuccessModalTotal"]}</span>
							<span className="uppercase">{discountPricing.total || totalPrice}</span>
						</div>
					</>
				);

			case "storage":
				return (
					<>
						<div className="flex flex-col gap-4 w-full py-4 px-7">
							<div className="flex justify-between">
								<span className="text-neutral-50 text-sm font-bold leading-none">
									{translations["CheckoutSuccessModalOrderSummary"]}
								</span>
								<span className="text-xs font-normal"> {formatCustomDate()}</span>
							</div>

							<div className="flex flex-col gap-1 pb-2 border-b border-dashed border-ipxon-light-blue-gray">
								<div className="flex justify-between">
									<span className="order-summary-information">
										{translations["CheckoutSuccessModalOrderSummaryProduct"]}
									</span>
									<span className="order-summary-information capitalize">
										{displayServiceTitle || "No plan Selected"}
									</span>
								</div>

								<div className="flex justify-between">
									<span className="order-summary-information">
										{translations["CheckoutSuccessModalOrderSummaryQuantity"]}
									</span>
									<span className="order-summary-information capitalize">
										{values.orderCount || "N/A"}
									</span>
								</div>

								<div className="flex justify-between">
									<span className="order-summary-information">
										{translations["CheckoutSuccessModalOrderSummaryLocation"]}
									</span>
									<div className="pl-1 flex gap-2 items-center rounded-lg w-fit text-zinc-200 text-sm">
										<ReactCountryFlag
											style={{
												fontSize: "1.15em",
												lineHeight: "1.15em",
												borderRadius: "80%",
												objectFit: "cover",
											}}
											countryCode={selectedLocation.iso_code}
											svg
										/>
										<span className="order-summary-information">{selectedLocation.name}</span>
									</div>
								</div>
							</div>

							<ul className="text-neutral-50 flex flex-col gap-1 text-sm pb-2 border-b border-dashed border-ipxon-light-blue-gray">
								<li>
									<h4 className="font-bold text-xs pb-1">
										{translations["CheckoutSuccessModalResources"]}
									</h4>
								</li>
								<li className="flex w-full justify-between">
									<span className="order-summary-information">{translations["NAS"]}</span>
									<span className="order-summary-information">{selectedPlan.nas.value}</span>
								</li>
							</ul>

							<ul className="flex flex-col gap-1">
								<li className="flex justify-between w-full">
									<span className="order-summary-information">
										{translations["CheckoutSuccessModalPaymentInstall"]}
									</span>
									<span className="text-zinc-400 text-sm capitalize">
										{translations["CheckoutSuccessModalPaymentInstallIncluded"]}
									</span>
								</li>

								<li className="flex justify-between w-full">
									<span className="order-summary-information">
										{translations["CheckoutSuccessModalPaymentCycle"]}
									</span>
									<span className="text-zinc-400 text-sm capitalize">{translatedPaymentCycle}</span>
								</li>

								{/* SUBTOTAL */}
								<li className="flex justify-between w-full mt-4">
									<span className="order-summary-information">
										{translations["CheckoutSuccessModalSubtotal"] ?? "Subtotal"}
									</span>
									<span className="text-zinc-400 text-sm uppercase">{totalPrice}</span>
								</li>

								{/* DISCOUNT (IF ANY) */}
								{discountPricing.discount && (
									<li className="flex justify-between w-full">
										<span className="order-summary-information">
											{translations["CheckoutSuccessModalDiscountApplied"] ??
												"Cupón de descuento aplicado"}
										</span>
										<span className="text-zinc-400 text-sm">{discountPricing.discount}</span>
									</li>
								)}
							</ul>
						</div>

						<div className="flex justify-between w-full px-7 py-4 bg-zinc-900 rounded-bl-lg rounded-br-lg">
							<span>{translations["CheckoutSuccessModalTotal"]}</span>
							<span className="uppercase">{discountPricing.total || totalPrice}</span>
						</div>
					</>
				);

			case "ultraproxy":
				return (
					<>
						<div className="flex flex-col gap-4 w-full py-4 px-7">
							<div className="flex justify-between">
								<span className="text-neutral-50 text-sm font-bold leading-none">
									{translations["CheckoutSuccessModalOrderSummary"]}
								</span>
								<span className="text-xs font-normal"> {formatCustomDate()}</span>
							</div>

							<div className="flex flex-col gap-1 pb-2 border-b border-dashed border-ipxon-light-blue-gray">
								<div className="flex justify-between">
									<span className="order-summary-information">
										{translations["CheckoutSuccessModalOrderSummaryProduct"]}
									</span>
									<span className="order-summary-information capitalize">
										{displayServiceTitle || "No plan Selected"}
									</span>
								</div>

								<div className="flex justify-between">
									<span className="order-summary-information">
										{translations["CheckoutSuccessModalOrderSummaryQuantity"]}
									</span>
									<span className="order-summary-information capitalize">
										{values.orderCount || "N/A"}
									</span>
								</div>

								<div className="flex justify-between">
									<span className="order-summary-information">
										{translations["CheckoutSuccessModalOrderSummaryLocation"]}
									</span>
									<div className="pl-1 flex gap-2 items-center rounded-lg w-fit text-zinc-200 text-sm">
										<ReactCountryFlag
											style={{
												fontSize: "1.15em",
												lineHeight: "1.15em",
												borderRadius: "80%",
												objectFit: "cover",
											}}
											countryCode={selectedLocation.iso_code}
											svg
										/>
										<span className="order-summary-information">{selectedLocation.name}</span>
									</div>
								</div>
							</div>

							<ul className="flex flex-col gap-1">
								<li className="flex justify-between w-full">
									<span className="order-summary-information">
										{translations["CheckoutSuccessModalPaymentInstall"]}
									</span>
									<span className="text-zinc-400 text-sm capitalize">
										{translations["CheckoutSuccessModalPaymentInstallIncluded"]}
									</span>
								</li>

								<li className="flex justify-between w-full">
									<span className="order-summary-information">
										{translations["CheckoutSuccessModalPaymentCycle"]}
									</span>
									<span className="text-zinc-400 text-sm capitalize">{translatedPaymentCycle}</span>
								</li>

								{/* SUBTOTAL */}
								<li className="flex justify-between w-full mt-4">
									<span className="order-summary-information">
										{translations["CheckoutSuccessModalSubtotal"] ?? "Subtotal"}
									</span>
									<span className="text-zinc-400 text-sm uppercase">{totalPrice}</span>
								</li>

								{/* DISCOUNT (IF ANY) */}
								{discountPricing.discount && (
									<li className="flex justify-between w-full">
										<span className="order-summary-information">
											{translations["CheckoutSuccessModalDiscountApplied"] ??
												"Cupón de descuento aplicado"}
										</span>
										<span className="text-zinc-400 text-sm">{discountPricing.discount}</span>
									</li>
								)}
							</ul>
						</div>

						<div className="flex justify-between w-full px-7 py-4 bg-zinc-900 rounded-bl-lg rounded-br-lg">
							<span>{translations["CheckoutSuccessModalTotal"]}</span>
							<span className="uppercase">{discountPricing.total || totalPrice}</span>
						</div>
					</>
				);

			case "bgp":
				return (
					<>
						<div className="flex flex-col gap-4 w-full py-4 px-7">
							<div className="flex justify-between">
								<span className="text-neutral-50 text-sm font-bold leading-none">
									{translations["CheckoutSuccessModalOrderSummary"]}
								</span>
								<span className="text-xs font-normal"> {formatCustomDate()}</span>
							</div>

							<div className="flex flex-col gap-1 pb-2 border-b border-dashed border-ipxon-light-blue-gray">
								<div className="flex justify-between">
									<span className="order-summary-information">
										{translations["CheckoutSuccessModalOrderSummaryProduct"]}
									</span>
									<span className="order-summary-information capitalize">
										{displayServiceTitle || "No plan Selected"}
									</span>
								</div>

								<div className="flex justify-between">
									<span className="order-summary-information">
										{translations["CheckoutSuccessModalOrderSummaryQuantity"]}
									</span>
									<span className="order-summary-information capitalize">
										{values.orderCount || "N/A"}
									</span>
								</div>

								<div className="flex justify-between">
									<span className="order-summary-information">
										{translations["CheckoutSuccessModalOrderSummaryLocation"]}
									</span>
									<div className="pl-1 flex gap-2 items-center rounded-lg w-fit text-zinc-200 text-sm">
										<ReactCountryFlag
											style={{
												fontSize: "1.15em",
												lineHeight: "1.15em",
												borderRadius: "80%",
												objectFit: "cover",
											}}
											countryCode={selectedLocation.iso_code}
											svg
										/>
										<span className="order-summary-information">{selectedLocation.name}</span>
									</div>
								</div>
							</div>

							<ul className="flex flex-col gap-1">
								<li className="flex justify-between w-full">
									<span className="order-summary-information">
										{translations["CheckoutSuccessModalPaymentInstall"]}
									</span>
									<span className="text-zinc-400 text-sm capitalize">
										{translations["CheckoutSuccessModalPaymentInstallIncluded"]}
									</span>
								</li>

								<li className="flex justify-between w-full">
									<span className="order-summary-information">
										{translations["CheckoutSuccessModalPaymentCycle"]}
									</span>
									<span className="text-zinc-400 text-sm capitalize">{translatedPaymentCycle}</span>
								</li>

								{/* 👇 ONLY shown if discount exists */}
								{/* SUBTOTAL */}
								<li className="flex justify-between w-full mt-4">
									<span className="order-summary-information">
										{translations["CheckoutSuccessModalSubtotal"] ?? "Subtotal"}
									</span>
									<span className="text-zinc-400 text-sm uppercase">{totalPrice}</span>
								</li>

								{/* DISCOUNT (IF ANY) */}
								{discountPricing.discount && (
									<li className="flex justify-between w-full">
										<span className="order-summary-information">
											{translations["CheckoutSuccessModalDiscountApplied"] ??
												"Cupón de descuento aplicado"}
										</span>
										<span className="text-zinc-400 text-sm">{discountPricing.discount}</span>
									</li>
								)}
							</ul>
						</div>

						<div className="flex justify-between w-full px-7 py-4 bg-zinc-900 rounded-bl-lg rounded-br-lg">
							<span>{translations["CheckoutSuccessModalTotal"]}</span>
							<span className="uppercase">{discountPricing.total || totalPrice}</span>
						</div>
					</>
				);

			case "iptransit":
				return (
					<>
						<div className="flex flex-col gap-4 w-full py-4 px-7">
							<div className="flex justify-between">
								<span className="text-neutral-50 text-sm font-bold leading-none">
									{translations["CheckoutSuccessModalOrderSummary"]}
								</span>
								<span className="text-xs font-normal"> {formatCustomDate()}</span>
							</div>

							<div className="flex flex-col gap-1 pb-2 border-b border-dashed border-ipxon-light-blue-gray">
								<div className="flex justify-between">
									<span className="order-summary-information">
										{translations["CheckoutSuccessModalOrderSummaryProduct"]}
									</span>
									<span className="order-summary-information capitalize">
										{displayServiceTitle || "No plan Selected"}
									</span>
								</div>

								<div className="flex justify-between">
									<span className="order-summary-information">
										{translations["CheckoutSuccessModalOrderSummaryQuantity"]}
									</span>
									<span className="order-summary-information capitalize">
										{values.orderCount || "N/A"}
									</span>
								</div>

								<div className="flex justify-between">
									<span className="order-summary-information">
										{translations["CheckoutSuccessModalOrderSummaryLocation"]}
									</span>
									<div className="pl-1 flex gap-2 items-center rounded-lg w-fit text-zinc-200 text-sm">
										<ReactCountryFlag
											style={{
												fontSize: "1.15em",
												lineHeight: "1.15em",
												borderRadius: "80%",
												objectFit: "cover",
											}}
											countryCode={selectedLocation.iso_code}
											svg
										/>
										<span className="order-summary-information">{selectedLocation.name}</span>
									</div>
								</div>
							</div>

							<ul className="flex flex-col gap-1">
								<li className="flex justify-between w-full">
									<span className="order-summary-information">
										{translations["CheckoutSuccessModalPaymentInstall"]}
									</span>
									<span className="text-zinc-400 text-sm capitalize">
										{translations["CheckoutSuccessModalPaymentInstallIncluded"]}
									</span>
								</li>

								<li className="flex justify-between w-full">
									<span className="order-summary-information">
										{translations["CheckoutSuccessModalPaymentCycle"]}
									</span>
									<span className="text-zinc-400 text-sm capitalize">{translatedPaymentCycle}</span>
								</li>

								{/* SUBTOTAL */}
								<li className="flex justify-between w-full mt-4">
									<span className="order-summary-information">
										{translations["CheckoutSuccessModalSubtotal"] ?? "Subtotal"}
									</span>
									<span className="text-zinc-400 text-sm uppercase">{totalPrice}</span>
								</li>

								{/* DISCOUNT (IF ANY) */}
								{discountPricing.discount && (
									<li className="flex justify-between w-full">
										<span className="order-summary-information">
											{translations["CheckoutSuccessModalDiscountApplied"] ??
												"Cupón de descuento aplicado"}
										</span>
										<span className="text-zinc-400 text-sm">{discountPricing.discount}</span>
									</li>
								)}
							</ul>
						</div>

						<div className="flex justify-between w-full px-7 py-4 bg-zinc-900 rounded-bl-lg rounded-br-lg">
							<span>{translations["CheckoutSuccessModalTotal"]}</span>
							<span className="uppercase">{discountPricing.total || totalPrice}</span>
						</div>
					</>
				);

			case "webhosting":
				return (
					<>
						<div className="flex flex-col gap-4 w-full py-4 px-7">
							<div className="flex justify-between">
								<span className="text-neutral-50 text-sm font-bold leading-none">
									{translations["CheckoutSuccessModalOrderSummary"]}
								</span>
								<span className="text-xs font-normal"> {formatCustomDate()}</span>
							</div>

							<div className="flex flex-col gap-1 pb-2 border-b border-dashed border-ipxon-light-blue-gray">
								<div className="flex justify-between">
									<span className="order-summary-information">
										{translations["CheckoutSuccessModalOrderSummaryProduct"]}
									</span>
									<span className="order-summary-information capitalize">
										{displayServiceTitle || "No plan Selected"}
									</span>
								</div>

								<div className="flex justify-between">
									<span className="order-summary-information">
										{translations["CheckoutSuccessModalOrderSummaryPlan"]}
									</span>
									<span className="order-summary-information capitalize">
										{displayWebHostingPlane || "Custom"}
									</span>
								</div>

								<div className="flex justify-between">
									<span className="order-summary-information">
										{translations["CheckoutSuccessModalOrderSummaryQuantity"]}
									</span>
									<span className="order-summary-information capitalize">
										{values.orderCount || "N/A"}
									</span>
								</div>

								<div className="flex justify-between">
									<span className="order-summary-information">
										{translations["CheckoutSuccessModalOrderSummaryLocation"]}
									</span>
									<div className="pl-1 flex gap-2 items-center rounded-lg w-fit text-zinc-200 text-sm">
										<ReactCountryFlag
											style={{
												fontSize: "1.15em",
												lineHeight: "1.15em",
												borderRadius: "80%",
												objectFit: "cover",
											}}
											countryCode={selectedLocation.iso_code}
											svg
										/>
										<span className="order-summary-information">{selectedLocation.name}</span>
									</div>
								</div>
							</div>

							<div className="flex flex-col pb-2 border-b border-dashed border-ipxon-light-blue-gray">
								<span className="font-bold text-xs pb-2">
									{translations["CheckoutSuccessModalResources"]}
								</span>

								{selectedPlan.webFeatures && (
									<ul className="flex flex-col gap-1 order-summary-information">
										{selectedPlan.webFeatures.map((feature) => (
											<li key={feature}>{translations[feature]}</li>
										))}
									</ul>
								)}
							</div>

							{values.configuration.serverName && (
								<ul className="pb-2 border-b border-dashed border-ipxon-light-blue-gray">
									<li className="flex flex-col gap-1 w-full">
										<h4 className="font-bold text-xs">
											{translations["CheckoutSuccessModalDomain"]}
										</h4>
										<span className="order-summary-information">
											{values.configuration.serverName?.toLowerCase()}
										</span>
									</li>
								</ul>
							)}

							<ul className="flex flex-col gap-1">
								<li className="flex justify-between w-full">
									<span className="order-summary-information">
										{translations["CheckoutSuccessModalPaymentCycle"]}
									</span>
									<span className="text-zinc-400 text-sm capitalize">{translatedPaymentCycle}</span>
								</li>
							</ul>
							{/* SUBTOTAL */}
							<li className="flex justify-between w-full mt-4">
								<span className="order-summary-information">
									{translations["CheckoutSuccessModalSubtotal"] ?? "Subtotal"}
								</span>
								<span className="text-zinc-400 text-sm uppercase">{totalPrice}</span>
							</li>

							{/* DISCOUNT (IF ANY) */}
							{discountPricing.discount && (
								<li className="flex justify-between w-full">
									<span className="order-summary-information">
										{translations["CheckoutSuccessModalDiscountApplied"] ??
											"Cupón de descuento aplicado"}
									</span>
									<span className="text-zinc-400 text-sm">{discountPricing.discount}</span>
								</li>
							)}
						</div>
						<div className="flex justify-between w-full px-7 py-4 bg-zinc-900 rounded-bl-lg rounded-br-lg">
							<span>{translations["CheckoutSuccessModalTotal"]}</span>
							<span className="uppercase">{discountPricing.total || totalPrice}</span>
						</div>
					</>
				);

			default:
				// Fallback if service type is unknown or not handled
				return (
					<div className="text-zinc-200 text-sm">{translations["CheckoutSuccessModalNoInfoAvailable"]}</div>
				);
		}
	};
	return (
		<div>
			<div className="flex flex-col items-center justify-center rounded-lg outline outline-1 outline-offset-[-1px] outline-zinc-800">
				{/* Title bar */}
				<div className="flex justify-between w-full px-7 py-4 bg-zinc-900 rounded-tl-lg rounded-tr-lg">
					<p className="text-sm text-neutral-50 font-normal">{userName}</p>
					<button onClick={handleOnLogout} className="text-sm text-neutral-50 font-bold">
						{translations["CheckoutSuccessModalLogOut"]}
					</button>
				</div>

				{/* Success message + Order info */}
				<div className="flex flex-col text-center border-b border-zinc-800 border-t w-full">
					<div className="py-4 px-9.5">
						<p className="font-bold pb-2 flex gap-2 justify-center items-center px-7 lg:px-0">
							<svg
								width="16"
								height="16"
								viewBox="0 0 16 16"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									d="M8 16C3.58857 16 0 12.4114 0 8C0 3.58857 3.58857 0 8 0C12.4114 0 16 3.58857 16 8C16 12.4114 12.4114 16 8 16ZM8 1.14286C4.21886 1.14286 1.14286 4.21886 1.14286 8C1.14286 11.7811 4.21886 14.8571 8 14.8571C11.7811 14.8571 14.8571 11.7811 14.8571 8C14.8571 4.21886 11.7811 1.14286 8 1.14286ZM7.26114 10.6897L11.8326 6.11829C12.056 5.89486 12.056 5.53371 11.8326 5.31029C11.6091 5.08686 11.248 5.08686 11.0246 5.31029L6.85714 9.47771L4.97543 7.596C4.752 7.37257 4.39086 7.37257 4.16743 7.596C3.944 7.81943 3.944 8.18057 4.16743 8.404L6.45314 10.6897C6.56457 10.8011 6.71086 10.8571 6.85714 10.8571C7.00343 10.8571 7.14971 10.8011 7.26114 10.6897Z"
									fill="#069E03"
								/>
							</svg>
							{translations["CheckoutSuccessModalOrderId"]} #{orderNumber}
						</p>
						<p className="text-zinc-500 text-sm font-normal">
							{translations["CheckoutSuccessModalDisclaimer"]}
						</p>
					</div>
				</div>

				{renderOrderInformation()}
			</div>
			<TertiaryOutlineButton
				type="button"
				onClick={() => {
					navigate("/");
				}}
				className="m-auto mt-7"
			>
				{translations["CheckoutSuccessBackHomeButton"]}
			</TertiaryOutlineButton>
		</div>
	);
};

export default OrderConfirmationSummary;
